import React, { useState } from 'react';
import { checkPasswordRequirements } from './passwordRequirements';
import './pages.css';

interface PasswordFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  disabled?: boolean;
  placeholder?: string;
  // Show the live rules checklist under the field (for choosing a new password).
  showRequirements?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const EyeIcon: React.FC<{ crossed: boolean }> = ({ crossed }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
    {crossed && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

// A password input with a show/hide toggle and an optional live rules checklist. The toggle is a
// real button (keyboard reachable, aria-pressed) and is disabled together with the input. As on the
// sign-up page, the checklist opens on focus and stays open after blur until every rule is met;
// unmet rules turn red once the field loses focus.
export const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  value,
  onChange,
  autoComplete,
  disabled,
  placeholder,
  showRequirements = false,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const [opened, setOpened] = useState(false);

  const { requirements, allMet } = checkPasswordRequirements(value);
  const listVisible = showRequirements && (focused || (opened && value.length > 0 && !allMet));
  const listID = `${id}-requirements`;
  const describedBy = [ariaDescribedBy, listVisible ? listID : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <>
      <div className="page-password-field">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setFocused(true);
            setOpened(true);
          }}
          onBlur={() => setFocused(false)}
          className="page-form-input page-password-input"
          required
          autoComplete={autoComplete}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
        />
        <button
          type="button"
          className="page-password-toggle"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
        >
          <EyeIcon crossed={visible} />
        </button>
      </div>
      {listVisible && (
        <ul id={listID} className="page-password-requirements">
          {requirements.map((req) => (
            <li
              key={req.id}
              className={`page-password-requirement${req.isMet ? ' met' : ''}${!focused && !req.isMet ? ' error' : ''}`}
            >
              <span className="page-password-requirement-mark" aria-hidden="true" />
              {req.label}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
